import {useCallback, useMemo, useState} from 'react'
import {useApolloClient, useMutation} from '@apollo/client'
import {toast} from 'react-toastify'

import {IMPERSONATE_USER, STOP_IMPERSONATION} from '../gql/mutations/authMutations'
import {
  ALL_PERMISSIONS,
  SESSION_SCOPED_STORAGE_KEYS,
  STORAGE_NAMES,
} from '../helpers/consts'
import {hasExactPermission} from '../helpers'
import {useGetUser} from '../store/userStore'
import {useUserPreferencesStore} from '../store/userPreferencesStore'
import type {
  IResImpersonateUser,
  IResStopImpersonation,
  TokenAuth,
} from '../types/auth'

/** Sesión del admin, guardada mientras dura la suplantación. */
export interface ImpersonationOrigin {
  session: TokenAuth
  targetLabel: string
  targetId: string | number
  startedAt: string
}

export const getImpersonationOrigin = (): ImpersonationOrigin | null => {
  const raw = window.localStorage.getItem(STORAGE_NAMES.IMPERSONATION_ORIGIN)
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as ImpersonationOrigin
    return parsed?.session?.token ? parsed : null
  } catch {
    return null
  }
}

const clearSessionScopedStorage = () => {
  SESSION_SCOPED_STORAGE_KEYS.forEach((key) => window.localStorage.removeItem(key))
}

const fullName = (user?: {firstName?: string; lastName?: string; email?: string} | null) => {
  if (!user) return ''
  const name = `${user.firstName || ''} ${user.lastName || ''}`.trim()
  return name || user.email || ''
}

/**
 * Permite a un admin abrir una sesión como otro usuario (sin su contraseña) y
 * después volver a la propia.
 *
 * El cambio de identidad se hace con una recarga completa de la página a
 * propósito: providers, WebSocket, Apollo y los stores de Zustand quedan
 * inicializados desde cero con la identidad nueva, en vez de arrastrar estado
 * del usuario anterior.
 */
export const useImpersonation = () => {
  const client = useApolloClient()
  const login = useGetUser((s) => s.login)
  const currentUser = useGetUser((s) => s.user?.user)
  const permissions = useGetUser((s) => s.user?.permissions)
  const sessionIsImpersonated = useGetUser((s) => s.user?.isImpersonated)

  const [busy, setBusy] = useState(false)
  const [impersonateUser] = useMutation<IResImpersonateUser>(IMPERSONATE_USER)
  const [stopImpersonationMutation] = useMutation<IResStopImpersonation>(STOP_IMPERSONATION)

  const origin = useMemo(() => getImpersonationOrigin(), [])
  const isImpersonating = Boolean(origin) || Boolean(sessionIsImpersonated)

  // Sin el bypass de `hasPermission` para administradores: el gate replica el
  // del backend. Los superusers lo pasan igual, porque Django les devuelve el
  // set completo de permisos (incluido `impersonate_user`).
  const canImpersonate = hasExactPermission(
    permissions || [],
    ALL_PERMISSIONS.IMPERSONATE_USER
  )

  const switchTo = useCallback(
    (session: TokenAuth, redirectTo: string) => {
      clearSessionScopedStorage()
      useUserPreferencesStore.getState().reset()
      login(session)
      // Recarga dura: garantiza que nada del usuario anterior sobreviva en memoria.
      window.location.assign(redirectTo)
    },
    [login]
  )

  /** Abre una sesión como `userId` y recarga la app con esa identidad. */
  const startImpersonation = useCallback(
    async (userId: string | number, targetLabel?: string, reason?: string) => {
      if (busy) return
      setBusy(true)
      try {
        const {data} = await impersonateUser({variables: {userId, reason: reason || null}})
        const session = data?.impersonateUser?.auth
        if (!session?.token) {
          toast.error('Could not start a session as this user')
          return
        }

        const adminSession = useGetUser.getState().user
        if (adminSession) {
          const payload: ImpersonationOrigin = {
            session: adminSession,
            targetLabel: targetLabel || fullName(session.user) || 'user',
            targetId: userId,
            startedAt: new Date().toISOString(),
          }
          window.localStorage.setItem(
            STORAGE_NAMES.IMPERSONATION_ORIGIN,
            JSON.stringify(payload)
          )
        }

        await client.clearStore()
        switchTo(session, '/dashboard')
      } catch (error: any) {
        toast.error(error?.message || 'Could not sign in as this user')
      } finally {
        setBusy(false)
      }
    },
    [busy, client, impersonateUser, switchTo]
  )

  /** Cierra la suplantación y vuelve a la sesión del admin. */
  const stopImpersonation = useCallback(async () => {
    if (busy) return
    setBusy(true)

    const storedOrigin = getImpersonationOrigin()
    let adminSession: TokenAuth | null = null

    try {
      // El backend re-emite la sesión del admin desde el claim del token, que es
      // más confiable que lo guardado en el navegador.
      const {data} = await stopImpersonationMutation()
      if (data?.stopImpersonation?.ok && data.stopImpersonation.auth?.token) {
        adminSession = data.stopImpersonation.auth
      }
    } catch {
      // Sin conexión o claim perdido tras un refresh de token: caemos a la
      // sesión de admin guardada localmente, que sigue siendo válida.
    }

    if (!adminSession) adminSession = storedOrigin?.session || null

    if (!adminSession?.token) {
      setBusy(false)
      toast.error('Could not restore your admin session. Please sign in again.')
      useGetUser.getState().logout()
      window.localStorage.removeItem(STORAGE_NAMES.IMPERSONATION_ORIGIN)
      window.location.assign('/login')
      return
    }

    window.localStorage.removeItem(STORAGE_NAMES.IMPERSONATION_ORIGIN)
    await client.clearStore()
    switchTo(adminSession, '/users')
  }, [busy, client, stopImpersonationMutation, switchTo])

  return {
    isImpersonating,
    canImpersonate,
    busy,
    origin,
    targetLabel: origin?.targetLabel || fullName(currentUser),
    adminLabel: fullName(origin?.session?.user),
    startImpersonation,
    stopImpersonation,
  }
}
